//
// FieldMetadata
//
// A text-input in code style.

import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useElementWidth } from '../../../../hooks/useElementWidth'
import { useStore } from '../../../../stores'

import { BooleanInput, Icon, Item } from '../../../../components'
import { AssetThumb } from '../../../../panels'
import { formatItemTitle } from '../../../../stores/data/utils'
import { mergeTransforms, Transform } from '../../../../stores/imageservers/utils'
import { ItemTitle } from '../../../components'

const ImageFieldMetadata = observer(function ImageFieldMetadata({
    metadataname,
    metadatafield,
    metadatavalue,
    worksheet,
}) {
    const { data } = useStore()
    const [containerRef, containerWidth] = useElementWidth(16)

    const [asset, setAsset] = useState(null)
    useEffect(() => {
        let isMounted = true
        let controller = new AbortController()
        async function asyncEffect() {
            setAsset(null)
            if (metadatavalue) {
                if (!data.assets.has(metadatavalue)) {
                    await data.fetchAssetByGid(metadatavalue, {
                        signal: controller.signal,
                    })
                }
                const asyncasset = data.assets.get(metadatavalue)
                if (isMounted) {
                    setAsset(asyncasset)
                }
            }
        }
        asyncEffect()
        return () => {
            controller.abort('Unmounted')
            isMounted = false
        }
    }, [metadatavalue, data])

    const componentformat = 'fit_square'

    // default: fit, square
    let transform_fit = 'fit'
    let transform_width = Math.max(containerWidth, 16)
    let transform_height = transform_width

    if (componentformat) {
        if (componentformat.startsWith('fill_')) {
            transform_fit = 'fill'
        }
        if (componentformat.endsWith('_banner')) {
            transform_height = (6 / 20) * transform_width
        }
        if (componentformat.endsWith('_landscape')) {
            transform_height = (9 / 16) * transform_width
        }
        if (componentformat.endsWith('_portrait')) {
            transform_height = (16 / 9) * transform_width
        }
        if (componentformat.endsWith('_pillar')) {
            transform_height = (20 / 6) * transform_width
        }
        if (componentformat === 'proportional' && asset) {
            const asset_width = asset.info.get('width')
            const asset_height = asset.info.get('height')
            if (asset_width && asset_height) {
                transform_height = (asset_height / asset_width) * transform_width
            }
        }
    }

    const transformResize = new Transform(
        transform_fit,
        transform_width,
        transform_height
    )
    const transform = asset
        ? mergeTransforms(asset.mainTransform, transformResize)
        : transformResize

    return (
        <div ref={containerRef}>
            {!asset ? undefined : (
                <AssetThumb
                    asset={asset}
                    transform={transform}
                    iconplaceholder="image"
                />
            )}
        </div>
    )
})

const FieldpickerFieldMetadata = observer(function FieldpickerFieldMetadata({
    metadataname,
    metadatafield,
    metadatavalue,
    worksheet,
}) {
    const { data, view } = useStore()

    const linkedfieldgids = metadatavalue ? metadatavalue.split(/\./) : []
    const linkedfields = linkedfieldgids.map(gid =>
        data.fields.has(gid) ? data.fields.get(gid) : undefined
    )

    const subfieldtitles = linkedfields.map((subfield, index) => {
        const title = subfield
            ? formatItemTitle(
                  subfield,
                  view.language,
                  view.environment.get('labelView')
              )
            : undefined
        if (!title) {
            return null
        }
        return (
            <Item key={index} indent={2 * index}>
                <Icon name="field" size="text" />{' '}
                <ItemTitle item={subfield} ellipsisAfter={32} />
            </Item>
        )
    })

    return <>{subfieldtitles}</>
})

export const FieldMetadata = observer(function FieldMetadata({
    metadataname,
    metadatafield,
    worksheet,
}) {
    if (!metadataname || !metadatafield) return null

    const language = worksheet.environment.get('language')
    const value = metadatafield.is_localized
        ? metadatafield.value[language]
        : metadatafield.value

    let metadatavalue
    switch (metadatafield.type) {
        case 'image':
            metadatavalue = (
                <ImageFieldMetadata
                    metadataname={metadataname}
                    metadatafield={metadatafield}
                    metadatavalue={value}
                    worksheet={worksheet}
                />
            )
            break
        case 'fieldpicker':
            metadatavalue = (
                <FieldpickerFieldMetadata
                    metadataname={metadataname}
                    metadatafield={metadatafield}
                    metadatavalue={value}
                    worksheet={worksheet}
                />
            )
            break
        case 'boolean':
            metadatavalue = (
                <>
                    <BooleanInput value={value} />
                    {metadataname}
                </>
            )
            break
        case 'textlist':
            metadatavalue = Array.isArray(value) ? (
                <ul>
                    {value.map((line, index) => (
                        <li key={index}>{line}</li>
                    ))}
                </ul>
            ) : (
                value
            )
            break
        case 'number':
        case 'decimal':
        case 'text':
        case 'textline':
        default:
            metadatavalue = value
    }

    return <span className={'ws-metaslot-' + metadatafield.type}>{metadatavalue}</span>
})
